import React, { useCallback, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useResource } from 'react-resource-router';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/standard-button';
import Tooltip from '@atlaskit/tooltip';

import { Caches, Usage } from 'src/components/pipelines/common';
import RunPipelineModal, {
  Schedules,
} from 'src/components/pipelines/components/run-pipeline';
import { ALLOWANCE_LIMIT } from 'src/components/pipelines/constants';
import { Allowance, Branch } from 'src/components/pipelines/models';
import {
  requestCaches,
  requestCreateSchedule,
  requestDeleteCache,
  requestDeleteCaches,
  requestDeleteSchedule,
  requestSchedules,
  requestToggleSchedule,
} from 'src/components/pipelines/redux/actions/pipelines';
import {
  getAccount,
  getCaches,
  getCapabilities,
  getIsFetchingCaches,
  getIsFetchingSchedules,
  getRepository,
  getSchedules,
  getScheduleStatus,
} from 'src/components/pipelines/redux/selectors/pipelines';
import { loadAllowanceResource } from 'src/components/pipelines/resources/allowance';
import { createPipeline } from 'src/components/pipelines/utils/create-pipeline';
import queryBranches from 'src/components/pipelines/utils/query-branches';
import { useHistory__UNSAFE, useParams } from 'src/router/hooks';

type Props = {
  selectedBranch?: Branch;
};

const PipelinesListActions = ({ selectedBranch }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory__UNSAFE();
  const { repositoryFullSlug } = useParams<{
    repositoryFullSlug: string;
  }>();

  const account = useSelector(getAccount);
  const capabilities = useSelector(getCapabilities);
  const repository = useSelector(getRepository);
  const caches = useSelector(getCaches);
  const isFetchingCaches = useSelector(getIsFetchingCaches);
  const schedules = useSelector(getSchedules);
  const scheduleStatus = useSelector(getScheduleStatus);
  const isFetchingSchedules = useSelector(getIsFetchingSchedules);
  const workspaceSlug = repositoryFullSlug?.split('/')[0] || '';
  const { data: allowanceData } = useResource(loadAllowanceResource);
  const defaultAllowance = new Allowance();
  const allowance = allowanceData ?? defaultAllowance;

  const [isRunPipelineModalOpen, setIsRunPipelineModalOpen] = useState(false);

  const fetchBranches = useCallback(
    (data: any) => queryBranches(repository, data.name),
    [repository]
  );

  const fetchCreatePipeline = useCallback(
    (data: any) => createPipeline(repository, data),
    [repository]
  );

  const isDisabled = useMemo(() => !repository.canWrite, [repository]);

  const openRunPipelineModal = useCallback(
    () => setIsRunPipelineModalOpen(true),
    [setIsRunPipelineModalOpen]
  );

  const closeRunPipelineModal = useCallback(
    () => setIsRunPipelineModalOpen(false),
    [setIsRunPipelineModalOpen]
  );

  const openPipelinePage = useCallback(
    (buildNumber: number) => {
      history.push({
        pathname: `/${repositoryFullSlug}/pipelines/results/${buildNumber}`,
        search: location.search,
      });
      setIsRunPipelineModalOpen(false);
    },
    [history, repositoryFullSlug, setIsRunPipelineModalOpen]
  );

  const fetchCaches = useCallback(() => dispatch(requestCaches()), [dispatch]);

  const deleteCaches = useCallback(
    (cacheGroupName: string) => {
      const cache = caches.find(c => c.name === cacheGroupName)!;
      dispatch(requestDeleteCaches(cache));
    },
    [dispatch, caches]
  );

  const deleteCache = useCallback(
    (cacheUuid: string, cacheGroupName: string) => {
      const cache = caches.find(c => c.name === cacheGroupName)!;
      dispatch(requestDeleteCache(cache, cacheUuid));
    },
    [dispatch, caches]
  );

  const fetchSchedules = useCallback(
    () => dispatch(requestSchedules()),
    [dispatch]
  );

  const createSchedule = useCallback(
    (schedule: any) => dispatch(requestCreateSchedule(schedule)),
    [dispatch]
  );

  const toggleSchedule = useCallback(
    (schedule: any) => dispatch(requestToggleSchedule(schedule)),
    [dispatch]
  );

  const deleteSchedule = useCallback(
    (schedule: any) => dispatch(requestDeleteSchedule(schedule)),
    [dispatch]
  );

  return (
    <ButtonGroup>
      <>
        <Tooltip
          content={
            isDisabled ? (
              <>
                To run pipeline you need <br /> write permission.{' '}
              </>
            ) : (
              ''
            )
          }
        >
          <Button onClick={openRunPipelineModal} isDisabled={isDisabled}>
            Run pipeline
          </Button>
        </Tooltip>
        {isRunPipelineModalOpen ? (
          <RunPipelineModal
            isPipelinesDisabled={!capabilities.pipelinesEnabled}
            isRepoReadOnly={!repository.canWrite}
            isOverAllowance={
              capabilities.limited &&
              capabilities.allowancePercentageUsed >= ALLOWANCE_LIMIT
            }
            fetchBranches={fetchBranches}
            fetchCreatePipeline={fetchCreatePipeline}
            openPipelinePage={openPipelinePage}
            onCloseDialog={closeRunPipelineModal}
            plansPageUrl={`/${workspaceSlug}/workspace/settings/plans-and-billing/`}
            settingsPageUrl={`/${repositoryFullSlug}/admin/pipelines/settings`}
            showBlanket
            refName={selectedBranch?.name}
            revision={selectedBranch?.target?.hash}
          />
        ) : null}
      </>
      <Schedules
        repository={repository}
        fetchingSchedules={isFetchingSchedules}
        schedules={schedules}
        scheduleStatus={scheduleStatus}
        getSchedules={fetchSchedules}
        createSchedule={createSchedule}
        deleteSchedule={deleteSchedule}
        enableSchedule={toggleSchedule}
        disableSchedule={toggleSchedule}
        fetchBranches={fetchBranches}
      />
      <Caches
        account={account}
        repository={repository}
        fetchingCaches={isFetchingCaches}
        caches={caches}
        getCaches={fetchCaches}
        deleteCache={deleteCache}
        deleteCaches={deleteCaches}
      />
      <Usage
        allowance={allowance}
        capabilities={capabilities}
        repository={repository}
      />
    </ButtonGroup>
  );
};

export default React.memo(PipelinesListActions);
